import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'src/store/reducers';

import TypesPersonalizationSettings from './TypesPersonalizationSettings';

import ErrorIcon from '@images/svg-icons/errorIcon.svg';

import * as actions from '@redux-actions/';

import {
    IPersonalizationSettings,
    PersonalizationSettingsPayload,
} from '@interfaces/myProfileInterfaces';

import { updatePersonalizationSettings } from '@services/myProfileServices';

import SuccessIconNoBackground from '@images/svg-icons/successIconNoBackground.svg';
import PreLoader from '@images/svg-icons/preLoader.svg';

import {
    DEFAULT_DESCRIPTIVE_SETTINGS,
    DEFAULT_BRAND_SETTINGS,
    DEFAULT_SHAPE_SETTINGS,
    DEFAULT_VISON_CARE_SETTINGS,
    DEFAULT_WHO_SETTINGS,
    StylePersonalizationSettingsConfig,
    DescriptivePersonalizationSettingsConfig,
    OthersPersonalizationSettingsConfig,
    BrandPersonalizationSettingsConfig,
    ShapePersonalizationSettingsConfig,
    VisionCarePersonalizationSettingsConfig,
    WhoPersonalizationSettingsConfig
} from '@utils/pages-utils/MyProfileUtils';
import { getLabelFallback } from '@utils/utils';

import { SubmitHandler, useForm, FormProvider } from 'react-hook-form';

import { cloneDeep } from 'lodash';

export interface Props {
    personalizationSettings: Partial<IPersonalizationSettings>;
}

const PersonalizationSettings = ({ personalizationSettings }: Props): JSX.Element => {
    const defaultBrandSettings = cloneDeep(DEFAULT_BRAND_SETTINGS);
    const defaultShapeSettings = cloneDeep(DEFAULT_SHAPE_SETTINGS);
    const defaultVisionCareSettings = cloneDeep(DEFAULT_VISON_CARE_SETTINGS);
    const defaultWhoSettings = cloneDeep(DEFAULT_WHO_SETTINGS);
    
    const methods = useForm();

    const { handleSubmit } = methods;

    const dispatch = useDispatch();

    const config = useSelector((state: RootState) => state.common.config);
    const updatePersonalizationSettingsRequestStatus = useSelector(
        (state: RootState) => state.myProfile.updatePersonalizationSettingsRequestStatus
    );
    const [nextStep, changeStep] = useState(0);
    const [errorNoCheck, setErrorNoCheck] = useState(false);
    const [settings, setSettings] = useState<{
        brandSettings: BrandPersonalizationSettingsConfig;
        shapeSettings: ShapePersonalizationSettingsConfig;
        visionCareSettings: VisionCarePersonalizationSettingsConfig;
        whoSettings: WhoPersonalizationSettingsConfig;
        isChanged: boolean;
    }>({
        brandSettings: defaultBrandSettings,
        shapeSettings: defaultShapeSettings,
        visionCareSettings: defaultVisionCareSettings,
        whoSettings: defaultWhoSettings,
        isChanged: false,
    });
    const [dataToSend, setDataToSend] = useState<PersonalizationSettingsPayload>({
        isMockRequest: config.mockRequest,
        storeId: config.storeId,
        catalogId: config.catalogId,
        langId: config.langId,
        URL: 'ContactInformation',
        errorViewName: 'ContactInformation',
        formName: 'PersonalizationForm',//'EmailPreferencesForm',
        emailOptintype: 2,
        sessionDuration: config.sessionDuration,
        logonId: `${config.storeId}|${config.userEmail}`,
    });

    /**
     * Set the default settings with the values retrieved from the service
     */

    useEffect(() => {
        if (personalizationSettings) {
            for (const [key, value] of Object.entries(personalizationSettings)) {
                if (settings.brandSettings.hasOwnProperty(key)) {
                    settings.brandSettings[key].value = value;
                }
                if (settings.shapeSettings.hasOwnProperty(key)) {
                    settings.shapeSettings[key].value = value;
                }
                if (settings.visionCareSettings.hasOwnProperty(key)) {
                    settings.visionCareSettings[key].value = value;
                }
                if (settings.whoSettings.hasOwnProperty(key)) {
                    settings.whoSettings[key].value = value;
                }
                setSettings({ ...settings, ...{ isChanged: true } });
            }
        }
    }, [personalizationSettings]);

    const getFormattedPayload = (data: IPersonalizationSettings) => {
        for (const [key, value] of Object.entries(data)) {
            dataToSend[`${key}_${dataToSend.storeId}_r_null`] = value;
            value ? (dataToSend[key] = 'on') : null;
            setDataToSend(dataToSend);
        }
    };

    const updateStep = ()  => {
        if( nextStep < 4 ){
            changeStep((nextStep) => nextStep + 1);
        }
    }

    const backStep = ()  => {
        if( nextStep > 0 ){
            if( nextStep  === 4){
                changeStep(3);
            }
            changeStep((nextStep) => nextStep - 1);
            actions.updatePersonalizationSettingsPending();
        }
    }

    const onSubmit: SubmitHandler<IPersonalizationSettings> = (data: IPersonalizationSettings) => {
        
        let noCheck: Boolean = false;
        
        
        for (const [key, values] of Object.entries(settings)) {
            for (const [key, value] of Object.entries(values)) {
                data[key] = value.value;
				 if (value.value){
                    noCheck = true;
                }
            }
        }
        if(noCheck) {
            setErrorNoCheck(false);
            getFormattedPayload(data);
            dispatch(updatePersonalizationSettings(dataToSend));
        } else {
            setErrorNoCheck(true);
        }
    };

    return (
        <div
            role="region"
            aria-label="Personalization settings form"
            className="personalization-settings"
        >
            <div className="content-container personalization-settings_container">
                <span className="secondary-title mb-24">
                    {getLabelFallback('SETTINGS_PERSONALIZATION_SETTINGS_TITLE')}
                </span>

                {nextStep > 0 && <button className="backButton" onClick={backStep}>
                    {getLabelFallback('SETTINGS_PERSONALIZATION_SETTINGS_FORM_BACK_QUESTION_CTA')}
                    <span className = "icon"></span>
                </button>}

                <div className="sectionIndex">
                <span className= {`circleSection first ${nextStep === 0 ? 'selected' : ''} ${nextStep > 0 ? 'done' : ''}`}><span>1</span></span>
                    <span className="lineSeparator"></span>
                    <span className={`circleSection ${nextStep === 1 ? 'selected' : ''} ${nextStep > 1 ? 'done' : ''}`}><span>2</span></span>
                    <span className="lineSeparator"></span>
                    <span className={`circleSection ${nextStep === 2 ? 'selected' : ''} ${nextStep > 2 ? 'done' : ''}`}><span>3</span></span>
                    <span className="lineSeparator"></span>
                    <span className={`circleSection last ${nextStep >= 3 ? 'selected' : ''} ${nextStep > 3 ? 'done' : ''}`}><span>4</span></span> 
                </div>
                
                <FormProvider {...methods}>
                    <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                        {settings.isChanged && (
                           <>
                                <div className={nextStep == 0 ? '': 'hidden-step'}> 
                                <TypesPersonalizationSettings
                                    settingsSectionTitle="SETTINGS_BRAND_PERSONALIZATION_SETTINGS_FORM_TITLE"
                                    settingsSectionType="style"
                                    settingsSettingsConfig={settings.brandSettings}
                                />
                                </div>

                                <div className={nextStep == 1 ? '': 'hidden-step'}> 
                                <TypesPersonalizationSettings
                                    settingsSectionTitle="SETTINGS_SHAPE_PERSONALIZATION_SETTINGS_FORM_TITLE"
                                    settingsSectionType="style"
                                    settingsSettingsConfig={settings.shapeSettings}
                                />
                                </div>

                                <div className={nextStep == 2 ? '': 'hidden-step'}> 
                                    <TypesPersonalizationSettings
                                        settingsSectionTitle="SETTINGS_VISION_CARE_PERSONALIZATION_SETTINGS_FORM_TITLE"
                                        settingsSectionType="style"
                                        settingsSettingsConfig={settings.visionCareSettings}
                                    />
                                </div>

                                <div className={nextStep >= 3 ? '': 'hidden-step'}> 
                                    <TypesPersonalizationSettings
                                        settingsSectionTitle="SETTINGS_WHO_PERSONALIZATION_SETTINGS_FORM_TITLE"
                                        settingsSectionType="style"
                                        settingsSettingsConfig={settings.whoSettings}
                                    />
                                </div>
                            </>
                        )}
                        {errorNoCheck && (
                            <div className='form-error-no-check'>
                                <div className='form-error-no-check-left'>
                                    <ErrorIcon />
                                </div>
                                <div className='form-error-no-check-right'>
                                    {getLabelFallback('MY_SETTINGS_PERSONALIZATION_ERROR')}
                                </div>
                            </div>
                        )}

 

                        <div className="form-footer">
                        {updatePersonalizationSettingsRequestStatus === 'SUCCESS' &&  nextStep >= 4  && (
                                    <div className="success-message">
                                        <SuccessIconNoBackground />
                                        <span className="secondary-message medium success bold">
                                            {getLabelFallback(
                                                'SETTINGS_PERSONALIZATION_SETTINGS_FORM_SUCCESS_MESSAGE'
                                            )}
                                        </span>
                                    </div>
                        )}

                            <button
                                data-element-id="Settings_Save"
                                className={`primary-button button-size-medium proceed ${updatePersonalizationSettingsRequestStatus === 'LOADING'
                                        ? 'loading'
                                        : ''
                                    }`}
                                type={`${nextStep > 3 ? "submit" : "button"}`}
                                onClick={updateStep}
                            >
                                {`${nextStep > 2 ?  getLabelFallback(
                                    'SETTINGS_PERSONALIZATION_SETTINGS_FORM_SAVE_CHANGES_CTA'
                                ) : getLabelFallback(
                                    'SETTINGS_PERSONALIZATION_SETTINGS_FORM_PROCEED_CTA'
                                )}`}
                                {updatePersonalizationSettingsRequestStatus === 'LOADING' && (
                                    <PreLoader />
                                )}

                            </button>

                            {nextStep <= 2  && <button 
                                className={`primary-button button-size-medium skip-question`}
                                type="button"
                                onClick={updateStep}
                            >
                            {getLabelFallback(
                                'SETTINGS_PERSONALIZATION_SETTINGS_FORM_SKIP_QUESTION_CTA'
                            )}
                            </button>}
                        </div>
                    </form>
                </FormProvider>
            </div>
        </div>
    );
};

export default PersonalizationSettings;
